import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { Navigate, Link, useNavigate } from "react-router-dom";

export default function Login() {
    const { user, login } = useAuth();
    const nav = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    if (user) return <Navigate to="/" replace />;

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        const res = await login(email, password);
        setLoading(false);
        if (!res.ok) setError(res.error);
        else nav("/");
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-[color:var(--bl-bg)] text-[color:var(--bl-text)] relative grain">
            <div
                className="hidden lg:flex flex-col justify-between relative overflow-hidden"
                style={{
                    backgroundImage:
                        "url(https://images.unsplash.com/photo-1442550528053-c431ecb55509?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400)",
                    backgroundSize: "cover",
                    backgroundPosition: "center",
                }}
            >
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(135deg, rgba(43,29,21,0.15) 0%, rgba(246,239,226,0.55) 60%, rgba(246,239,226,0.9) 100%)",
                    }}
                />
                <div className="relative z-10 px-12 py-10">
                    <div className="flex items-center gap-4">
                        <img src="/bull.png" alt="" data-testid="brand-logo" className="w-20 h-20 object-contain rounded-full bg-[color:var(--bl-bg)] shadow-md p-1" />
                        <div>
                            <div className="font-serif-display text-3xl text-[color:var(--bl-text)]">
                                Takealot Insight
                            </div>
                            <div className="text-[10px] uppercase tracking-[0.22em] text-[color:var(--bl-takealot-orange)] font-medium mt-1">
                                Brothers Coffee Roastery
                            </div>
                        </div>
                    </div>
                </div>
                <div className="relative z-10 px-12 py-12">
                    <p className="font-serif-display text-3xl leading-snug max-w-md text-[color:var(--bl-text)]">
                        “Roast slow. Sell sharp.”
                    </p>
                    <p className="mt-3 text-sm text-[color:var(--bl-text-dim)] max-w-md">
                        Track revenue, fees, stock cover, kg sold and net profit per product across
                        JHB, CPT and DBN — all from your Takealot Seller API.
                    </p>
                </div>
            </div>

            <div className="flex items-center justify-center p-6 lg:p-12">
                <form
                    onSubmit={onSubmit}
                    className="w-full max-w-md border border-[color:var(--bl-border)] bg-[color:var(--bl-surface)] p-8 rounded-sm shadow-sm"
                    data-testid="login-form"
                >
                    <h1 className="font-serif-display text-3xl mb-1 text-[color:var(--bl-text)]">
                        Sign in
                    </h1>
                    <p className="text-sm text-[color:var(--bl-text-dim)] mb-6">
                        Welcome back. Pull a fresh report.
                    </p>

                    {error && (
                        <div
                            data-testid="login-error"
                            className="mb-4 p-3 text-sm bg-[color:var(--bl-danger)]/10 border border-[color:var(--bl-danger)]/40 text-[color:var(--bl-danger)] rounded-sm"
                        >
                            {error}
                        </div>
                    )}

                    <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-2">
                        Email
                    </label>
                    <input
                        data-testid="login-email-input"
                        type="email"
                        autoComplete="email"
                        required
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)] mb-4 text-[color:var(--bl-text)]"
                    />

                    <label className="block text-xs uppercase tracking-wider text-[color:var(--bl-text-dim)] mb-2">
                        Password
                    </label>
                    <input
                        data-testid="login-password-input"
                        type="password"
                        autoComplete="current-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="w-full bg-[color:var(--bl-bg)] border border-[color:var(--bl-border)] px-3 py-2 rounded-sm focus:outline-none focus:border-[color:var(--bl-takealot-blue)] mb-6 text-[color:var(--bl-text)]"
                    />

                    <button
                        data-testid="login-submit-btn"
                        disabled={loading}
                        type="submit"
                        className="w-full bg-[color:var(--bl-takealot-blue)] text-white font-medium py-2.5 rounded-sm hover:brightness-110 transition disabled:opacity-50"
                    >
                        {loading ? "Signing in…" : "Sign in"}
                    </button>

                    <div className="mt-4 flex items-center justify-between text-xs">
                        <Link data-testid="forgot-password-link" to="/forgot-password" className="text-[color:var(--bl-takealot-blue)] hover:underline">
                            Forgot password?
                        </Link>
                        <Link data-testid="forgot-email-link" to="/forgot-email" className="text-[color:var(--bl-takealot-orange)] hover:underline">
                            Forgot your email?
                        </Link>
                    </div>

                    <div className="mt-6 text-xs text-[color:var(--bl-text-dim)] text-center">
                        Need an account?{" "}
                        <Link
                            data-testid="goto-register-link"
                            to="/register"
                            className="text-[color:var(--bl-takealot-orange)] hover:underline"
                        >
                            Ask your admin
                        </Link>
                    </div>
                </form>
            </div>
        </div>
    );
}
